import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export type ProcessOptionsType = (options: string[]) => {
  value: string;
  label: string;
}[];

const COLORS = [
  // leaf palette
  { value: "#0C5BA8", name: "" },
  { value: "#4D792E", name: "" },
  { value: "#EA573B", name: "" },
  { value: "#2D6F7C", name: "" },

  // Ardoise / Neutres
  { value: "#e5e7eb", name: "lightGray" },
  { value: "#64748b", name: "gray" },
  { value: "#0f172a", name: "darkGray" },

  // Bleu
  { value: "#93c5fd", name: "lightBlue" },
  { value: "#2563eb", name: "blue" },
  { value: "#1e40af", name: "darkBlue" },

  // Cyan
  { value: "#67e8f9", name: "lightCyan" },
  { value: "#0ea5e9", name: "cyan" },
  { value: "#075985", name: "darkCyan" },

  // Vert
  { value: "#6ee7b7", name: "lightGreen" },
  { value: "#10b981", name: "green" },
  { value: "#065f46", name: "darkGreen" },

  // Lime
  { value: "#bef264", name: "lightLime" },
  { value: "#84cc16", name: "lime" },
  { value: "#3f6212", name: "darkLime" },

  // Ambre
  { value: "#fde68a", name: "lightAmber" },
  { value: "#f59e0b", name: "amber" },
  { value: "#92400e", name: "darkAmber" },

  // Orange
  { value: "#fdba74", name: "lightOrange" },
  { value: "#f97316", name: "orange" },
  { value: "#9a3412", name: "darkOrange" },

  // Rouge
  { value: "#fca5a5", name: "lightRed" },
  { value: "#ef4444", name: "red" },
  { value: "#7f1d1d", name: "darkRed" },

  // Rose
  { value: "#fbcfe8", name: "lightPink" },
  { value: "#ec4899", name: "pink" },
  { value: "#9d174d", name: "darkPink" },

  // Violet
  { value: "#ddd6fe", name: "lightPurple" },
  { value: "#a855f7", name: "purple" },
  { value: "#5b21b6", name: "darkPurple" },
];

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function isPreset(color: string | null | undefined) {
  return COLORS.some((c) => c.value.toLowerCase() === color?.toLowerCase());
}

function ColorDot({
  color,
  name,
  selected,
  onClick,
}: {
  color: string;
  name: string;
  selected?: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative h-8 w-8 rounded-full transition
        hover:scale-110 focus:outline-none
        ${selected ? "ring-2 ring-offset-2 ring-slate-400" : ""}
      `}
      style={{ backgroundColor: color }}
    >
      {/* Tooltip */}
      {name?.length > 0 && (
        <span
          className="
          pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2
          whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white
          opacity-0 transition group-hover:opacity-100
        "
        >
          {t(`components.colorSelect.${name}`)}
        </span>
      )}
    </button>
  );
}

type ColorPaletteProps = {
  value?: string | null;
  onChange?: (color: string | null) => void;
  className?: string;
};

function ColorSelect({ value, onChange, className }: ColorPaletteProps) {
  const { t } = useTranslation();

  const [customInput, setCustomInput] = useState<string>(() =>
    value && !isPreset(value) ? value : "",
  );

  // Sync si la valeur externe change vers une couleur custom (ex: rechargement)
  useEffect(() => {
    if (value && !isPreset(value)) {
      setCustomInput(value);
    }
  }, [value]);

  const isCustomSelected = !!value && !isPreset(value);
  const customValid = HEX_RE.test(customInput);

  function handleCustomChange(raw: string) {
    setCustomInput(raw);
    if (HEX_RE.test(raw)) {
      onChange?.(raw.toLowerCase());
    }
  }

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {COLORS.map((c) => (
        <div key={c.value} className="group">
          <ColorDot
            color={c.value}
            name={c.name}
            selected={value?.toLowerCase() === c.value.toLowerCase()}
            onClick={() => {
              setCustomInput("");
              onChange?.(c.value);
            }}
          />
        </div>
      ))}

      {/* Couleur custom */}
      <div className="flex items-center gap-1.5">
        {/* Aperçu swatch */}
        <div
          className={`h-8 w-8 flex-shrink-0 rounded-full border border-slate-200 transition ${
            isCustomSelected ? "ring-2 ring-offset-2 ring-slate-400" : ""
          }`}
          style={{
            backgroundColor: customValid ? customInput : "transparent",
            backgroundImage: customValid
              ? "none"
              : "repeating-linear-gradient(45deg,#d1d5db 0,#d1d5db 2px,transparent 0,transparent 50%)",
            backgroundSize: "6px 6px",
          }}
        />
        <input
          type="text"
          value={customInput}
          onChange={(e) => handleCustomChange(e.target.value)}
          placeholder={t("components.colorSelect.customPlaceholder")}
          maxLength={7}
          className={`h-8 w-24 rounded border px-2 text-sm focus:outline-none focus:ring-1 ${
            customInput && !customValid
              ? "border-red-400 focus:ring-red-400"
              : "border-slate-300 focus:ring-slate-400"
          }`}
        />
      </div>
    </div>
  );
}

export default ColorSelect;
